import React, { useState, useEffect } from 'react';
import api from '../lib/api';
import { Save, Settings, Building2, Brain, Mail, Send, CheckCircle, AlertTriangle, Upload, Image, Trash2 } from 'lucide-react';

export default function SettingsPage() {
  const [form, setForm] = useState({ company_name: '', company_nif: '', company_address: '', company_phone: '', company_email: '', company_website: '', company_logo: '', ai_provider: 'openai', ai_model: 'gpt-4o', ai_api_key: '', resend_api_key: '', sender_email: '', notification_email: '', smtp_host: '', smtp_port: '', smtp_user: '', smtp_pass: '', imap_host: '', imap_port: '', imap_user: '', imap_pass: '', email_signature: '', email_template_budget: '' });
  const [saved, setSaved] = useState(false);
  const [sending, setSending] = useState(false);
  const [sendResult, setSendResult] = useState(null);

  useEffect(() => { api.get('/settings').then(r => { if (r.data) setForm(p => ({ ...p, ...r.data })); }).catch(() => {}); }, []);

  const save = async () => {
    await api.put('/settings', form);
    setSaved(true); setTimeout(() => setSaved(false), 2000);
  };

  const sendAlerts = async () => {
    setSending(true); setSendResult(null);
    try {
      const { data } = await api.post('/notifications/send-alerts');
      setSendResult(data);
    } catch (e) {
      setSendResult({ success: false, message: 'Erro ao enviar' });
    } finally { setSending(false); }
  };

  const set = (k, v) => setForm(p => ({ ...p, [k]: v }));

  return (
    <div className="animate-in pt-10 lg:pt-0">
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="text-xl font-bold">Definicoes</h1>
          <p className="text-sm" style={{ color: 'var(--text-secondary)' }}>Configuracao do sistema</p>
        </div>
        <button data-testid="save-settings-btn" onClick={save} className="btn btn-primary">
          {saved ? <><CheckCircle size={16} /> Guardado</> : <><Save size={16} /> Guardar</>}
        </button>
      </div>

      <div className="space-y-4">
        {/* Company settings */}
        <div className="pro-card-padded" data-testid="company-settings">
          <h2 className="text-sm font-semibold mb-3 flex items-center gap-2"><Building2 size={16} /> Dados da Empresa</h2>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
            {/* Logo Upload Section */}
            <div className="md:row-span-3 flex flex-col items-center justify-center p-4 rounded-md" style={{ background: 'var(--surface-alt)', border: '1px solid var(--border-light)' }}>
              <label className="field-label text-center mb-2">Logo da Empresa</label>
              {form.company_logo ? (
                <div className="relative group">
                  <img 
                    src={form.company_logo.startsWith('data:') ? form.company_logo : `data:image/png;base64,${form.company_logo}`} 
                    alt="Logo" 
                    className="w-32 h-32 object-contain rounded-md border"
                    style={{ background: '#fff', borderColor: 'var(--border)' }}
                  />
                  <div className="absolute inset-0 bg-black/50 opacity-0 group-hover:opacity-100 transition-opacity rounded-md flex items-center justify-center gap-2">
                    <label className="btn btn-outline btn-xs cursor-pointer" style={{ background: '#fff' }}>
                      <Upload size={12} /> Alterar
                      <input 
                        type="file" 
                        accept="image/*" 
                        className="hidden" 
                        onChange={(e) => {
                          const file = e.target.files?.[0];
                          if (!file) return;
                          const reader = new FileReader();
                          reader.onload = () => set('company_logo', reader.result);
                          reader.readAsDataURL(file);
                        }} 
                      />
                    </label>
                    <button 
                      onClick={() => set('company_logo', '')} 
                      className="btn btn-danger btn-xs"
                    >
                      <Trash2 size={12} />
                    </button>
                  </div>
                </div>
              ) : (
                <label className="w-32 h-32 flex flex-col items-center justify-center rounded-md border-2 border-dashed cursor-pointer hover:border-blue-400 transition-colors" style={{ borderColor: 'var(--border)' }}>
                  <Image size={24} style={{ color: 'var(--text-light)' }} />
                  <span className="text-[10px] mt-2 text-center" style={{ color: 'var(--text-light)' }}>Clique para<br/>fazer upload</span>
                  <input 
                    type="file" 
                    accept="image/*" 
                    className="hidden" 
                    onChange={(e) => {
                      const file = e.target.files?.[0];
                      if (!file) return;
                      const reader = new FileReader();
                      reader.onload = () => set('company_logo', reader.result);
                      reader.readAsDataURL(file);
                    }} 
                  />
                </label>
              )}
              <p className="text-[9px] mt-2 text-center" style={{ color: 'var(--text-light)' }}>PNG, JPG ou SVG<br/>Max 500KB</p>
            </div>
            
            {/* Company Details */}
            <div><label className="field-label">Nome da Empresa</label><input className="input" value={form.company_name} onChange={e => set('company_name', e.target.value)} /></div>
            <div><label className="field-label">NIF</label><input className="input" value={form.company_nif} onChange={e => set('company_nif', e.target.value)} /></div>
            <div className="md:col-span-2"><label className="field-label">Morada</label><input className="input" value={form.company_address} onChange={e => set('company_address', e.target.value)} /></div>
            <div><label className="field-label">Telefone</label><input className="input" value={form.company_phone || ''} onChange={e => set('company_phone', e.target.value)} /></div>
            <div><label className="field-label">Email</label><input className="input" type="email" value={form.company_email || ''} onChange={e => set('company_email', e.target.value)} /></div>
            <div className="md:col-span-2"><label className="field-label">Website</label><input className="input" value={form.company_website || ''} onChange={e => set('company_website', e.target.value)} placeholder="https://www.empresa.pt" /></div>
          </div>
        </div>

        {/* AI settings */}
        <div className="pro-card-padded" data-testid="ai-settings">
          <h2 className="text-sm font-semibold mb-3 flex items-center gap-2"><Brain size={16} /> Configuracao AI</h2>
          <p className="text-xs mb-3" style={{ color: 'var(--text-light)' }}>Opcional: se nao configurado, usa a chave AI padrao do sistema.</p>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
            <div><label className="field-label">Fornecedor</label>
              <select className="select" value={form.ai_provider} onChange={e => set('ai_provider', e.target.value)}>
                <option value="openai">OpenAI</option><option value="anthropic">Anthropic</option><option value="google">Google</option>
              </select></div>
            <div><label className="field-label">Modelo</label><input className="input" value={form.ai_model} onChange={e => set('ai_model', e.target.value)} placeholder="gpt-4o" /></div>
            <div><label className="field-label">API Key</label><input className="input" type="password" value={form.ai_api_key} onChange={e => set('ai_api_key', e.target.value)} placeholder="sk-..." /></div>
          </div>
        </div>

        {/* Email notification settings */}
        <div className="pro-card-padded" data-testid="email-settings">
          <h2 className="text-sm font-semibold mb-3 flex items-center gap-2"><Mail size={16} /> Notificacoes por Email (Resend)</h2>
          <p className="text-xs mb-3" style={{ color: 'var(--text-light)' }}>
            Configure o Resend para alertas automaticos.
            <a href="https://resend.com" target="_blank" rel="noopener noreferrer" className="ml-1 underline" style={{ color: 'var(--accent)' }}>Obter chave</a>
          </p>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-3 mb-3">
            <div><label className="field-label">Resend API Key</label><input className="input" type="password" value={form.resend_api_key} onChange={e => set('resend_api_key', e.target.value)} placeholder="re_..." /></div>
            <div><label className="field-label">Email Remetente</label><input className="input" value={form.sender_email} onChange={e => set('sender_email', e.target.value)} placeholder="noreply@empresa.pt" /></div>
            <div><label className="field-label">Email Notificacoes</label><input className="input" value={form.notification_email} onChange={e => set('notification_email', e.target.value)} placeholder="admin@empresa.pt" /></div>
          </div>
          <div className="flex items-center gap-3">
            <button data-testid="send-alerts-btn" onClick={sendAlerts} disabled={sending} className="btn btn-outline btn-sm">
              {sending ? <><span className="animate-spin inline-block w-3 h-3 border-2 border-current border-t-transparent rounded-full mr-1" /> A enviar...</> : <><Send size={14} /> Enviar Alertas Agora</>}
            </button>
            {sendResult && (
              <span className={`text-xs font-medium flex items-center gap-1 ${sendResult.success ? 'text-green-600' : 'text-red-600'}`}>
                {sendResult.success ? <CheckCircle size={14} /> : <AlertTriangle size={14} />}
                {sendResult.message}
              </span>
            )}
          </div>
        </div>

        {/* SMTP/IMAP Settings */}
        <div className="pro-card-padded" data-testid="smtp-settings">
          <h2 className="text-sm font-semibold mb-1 flex items-center gap-2"><Mail size={16} /> Servidor Email (SMTP/IMAP)</h2>
          <p className="text-xs mb-3" style={{ color: 'var(--text-light)' }}>Configure o servidor SMTP para envio de emails (orcamentos, alertas). IMAP para recepcao.</p>
          <div className="p-3 rounded-md mb-3 text-xs" style={{ background: '#EFF6FF', border: '1px solid #BFDBFE' }}>
            <p className="font-semibold mb-1" style={{ color: '#1E40AF' }}>Portas comuns:</p>
            <div className="grid grid-cols-2 gap-1" style={{ color: '#1D4ED8' }}>
              <span>SMTP TLS: 587 (Gmail, Outlook)</span>
              <span>SMTP SSL: 465 (Gmail)</span>
              <span>IMAP SSL: 993 (Gmail, Outlook)</span>
              <span>POP3 SSL: 995 (Gmail)</span>
            </div>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-4 gap-3 mb-2">
            <div><label className="field-label">SMTP Host</label><input className="input" value={form.smtp_host || ''} onChange={e => set('smtp_host', e.target.value)} placeholder="smtp.gmail.com" /></div>
            <div><label className="field-label">SMTP Porta</label><input className="input" value={form.smtp_port || ''} onChange={e => set('smtp_port', e.target.value)} placeholder="587" /></div>
            <div><label className="field-label">SMTP Utilizador</label><input className="input" value={form.smtp_user || ''} onChange={e => set('smtp_user', e.target.value)} placeholder="seuemail@gmail.com" /></div>
            <div><label className="field-label">SMTP Senha / App Password</label><input className="input" type="password" value={form.smtp_pass || ''} onChange={e => set('smtp_pass', e.target.value)} placeholder="App password" /></div>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-4 gap-3">
            <div><label className="field-label">IMAP Host</label><input className="input" value={form.imap_host || ''} onChange={e => set('imap_host', e.target.value)} placeholder="imap.gmail.com" /></div>
            <div><label className="field-label">IMAP Porta</label><input className="input" value={form.imap_port || ''} onChange={e => set('imap_port', e.target.value)} placeholder="993" /></div>
            <div><label className="field-label">IMAP Utilizador</label><input className="input" value={form.imap_user || ''} onChange={e => set('imap_user', e.target.value)} /></div>
            <div><label className="field-label">IMAP Senha</label><input className="input" type="password" value={form.imap_pass || ''} onChange={e => set('imap_pass', e.target.value)} /></div>
          </div>
        </div>

        {/* Email Templates */}
        <div className="pro-card-padded" data-testid="email-template-settings">
          <h2 className="text-sm font-semibold mb-3 flex items-center gap-2"><Mail size={16} /> Assinatura e Modelo Email</h2>
          <div className="space-y-3">
            <div>
              <label className="field-label">Assinatura do Email</label>
              <textarea className="input" rows={3} value={form.email_signature || ''} onChange={e => set('email_signature', e.target.value)} placeholder="Com os melhores cumprimentos,&#10;[Nome]&#10;[Empresa] | [Telefone] | [Website]" />
            </div>
            <div>
              <label className="field-label">Modelo Email Orcamento (corpo)</label>
              <p className="text-[10px] mb-1" style={{ color: 'var(--text-light)' }}>Variaveis disponiveis: {'{'}cliente{'}'}, {'{'}descricao{'}'}, {'{'}total{'}'}, {'{'}portal_link{'}'}, {'{'}empresa{'}'}</p>
              <textarea className="input" rows={5} value={form.email_template_budget || ''} onChange={e => set('email_template_budget', e.target.value)} placeholder="Exmo(a) Sr(a) {cliente},&#10;&#10;Em anexo enviamos o orcamento solicitado para {descricao} no valor de {total} EUR.&#10;&#10;Pode consultar, aceitar ou rejeitar o orcamento atraves do link:&#10;{portal_link}&#10;&#10;O orcamento tem validade de 30 dias." />
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
